
import { defineComponent } from 'vue';
import { IVatTuResponseBase } from '@/core/interfaces/ApiResponses';
import { VatTuService } from '@/core/services/VatTu.service';
import DanhMucVatTuService from '@/core/services/DanhMucVatTu.service';
import { swalErrNotification } from '@/core/helpers/utils';
import { generateTablePdf } from '@/core/helpers/generate-table-pdf';
import PdfViewerPage from '@/components/shared/pdf-viewer/PdfViewerPage.vue';
import { PDF_GENERATE_TABLE_DEFAULTS } from '@/components/shared/pdf-viewer/pdf-content.config';
import { pickDonViPdfExtra } from '@/components/shared/pdf-viewer/pdf-document-header';

/** Column layout for PDF export (list view, no actions / no số dư / no trạng thái). */
const VAT_TU_LIST_PDF_COLUMNS = [
  { header: 'STT', width: 22, align: 'center' as const },
  { header: 'Tên', width: 95, align: 'left' as const },
  { header: 'Mã', width: 32, align: 'left' as const },
  { header: 'ĐVT', width: 22, align: 'center' as const },
  { header: 'Thu hồi', width: 18, align: 'center' as const, type: 'checkbox' as const },
  { header: 'C/ty quản lý', width: 24, align: 'center' as const, type: 'checkbox' as const },
  { header: 'Nhóm', width: 40, align: 'left' as const },
];

export default defineComponent({
  name: 'VatTuListViewer',

  components: { PdfViewerPage },

  data() {
    return {
      vatTuItems: [] as IVatTuResponseBase[],
      tenDanhMuc: '',
      pdfSource: null as string | null,
      isGenerating: false,
      tenDonVi: '',
      tenDonViCha: '',
    };
  },

  computed: {
    pdfFileName(): string {
      const slug = this.tenDanhMuc
        ? this.tenDanhMuc.replace(/\s+/g, '-').toLowerCase()
        : 'danh-sach';
      return `danh-sach-vat-tu-${slug}.pdf`;
    },
  },

  methods: {
    formatSttCell(index: number): string {
      return String(index + 1);
    },

    formatNhomCell(item: IVatTuResponseBase): string {
      return item.danh_muc_vat_tu?.[0]?.ten_danh_muc ?? '-';
    },

    async loadDanhMucName(id: number) {
      const queryTen = this.$route.query.ten;
      if (typeof queryTen === 'string' && queryTen) {
        this.tenDanhMuc = queryTen;
        return;
      }

      if (!id) {
        this.tenDanhMuc = 'Tất cả nhóm';
        return;
      }

      try {
        const {
          data: { data },
        } = await DanhMucVatTuService.get(id);
        this.tenDanhMuc = data.ten_danh_muc;
      } catch {
        this.tenDanhMuc = '';
      }
    },

    async loadAndGenerate() {
      const idDmVatTu = Number(this.$route.params.idDmVatTu);

      try {
        this.isGenerating = true;
        this.pdfSource = null;

        await this.loadDanhMucName(idDmVatTu);
        const listResult = await VatTuService.listAllByDanhMuc(
          idDmVatTu,
          idDmVatTu ? 100 : 1000
        );
        this.vatTuItems = listResult.items;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(listResult.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;
        this.pdfSource = await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, 'Có lỗi khi tải danh sách vật tư');
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf(): Promise<string> {
      const rows = this.vatTuItems.map((item, index) => [
        this.formatSttCell(index),
        item.ten_vat_tu ?? '',
        item.ma_vat_tu ?? '',
        item.don_vi_tinh ? item.don_vi_tinh : '-',
        !!item.thu_hoi,
        !!item.cong_ty_duyet,
        this.formatNhomCell(item),
      ]);

      return generateTablePdf({
        ...PDF_GENERATE_TABLE_DEFAULTS,
        title: 'DANH SÁCH VẬT TƯ',
        subtitle: this.tenDanhMuc ? `Nhóm vật tư: ${this.tenDanhMuc}` : undefined,
        columns: VAT_TU_LIST_PDF_COLUMNS,
        rows,
        documentHeader: {
          tenDonVi: this.tenDonVi,
          tenDonViCha: this.tenDonViCha,
          variant: 'masterList',
        },
      });
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
